import React, { useState } from 'react';
import { useBranches } from '../context/BranchContext';
import useAsync from '../hooks/useAsync';
import api from '../services/api';
import DetailTable from '../components/analytics/DetailTable';
import Modal from '../components/common/Modal';
import { BillStatus } from '../components/common/Badges';
import { Loader, Notice } from '../components/common/Feedback';
import { fmtDate } from '../utils/dates';
import { formatMoney } from '../utils/money';

const n = (_r, i) => i + 1;
const who = (r) => (
  <>
    {r.customer || r.name || 'Guest'}
    <small>{r.phone || ''}</small>
  </>
);
const when = (r) => fmtDate(r.created_at || r.last_visit);

const TILES = [
  { key: 'customers', label: 'Customers', listKey: 'customer_list', columns: [
    { label: '#', render: n }, { label: 'Customer', render: who }, { label: 'Visits', align: 'right', render: (r) => r.visits },
    { label: 'Last visit', render: when },
  ] },
  { key: 'services_taken', label: 'Services taken', listKey: 'service_list', columns: [
    { label: '#', render: n }, { label: 'Service', render: (r) => r.service }, { label: 'Customer', render: who },
    { label: 'Qty', align: 'right', render: (r) => r.quantity || 1 }, { label: 'Date', render: when },
  ] },
  { key: 'payment_completed', label: 'Payments completed', listKey: 'payment_list', columns: [
    { label: '#', render: n }, { label: 'Customer', render: who }, { label: 'Services', render: (r) => r.services },
    { label: 'Paid', align: 'right', render: (r) => formatMoney(r.paid) }, { label: 'Date', render: when },
  ] },
  { key: 'pending_total', label: 'Pending amount', money: true, listKey: 'pending_list', columns: [
    { label: '#', render: n }, { label: 'Customer', render: who },
    { label: 'Pending', align: 'right', render: (r) => formatMoney(r.pending) }, { label: 'Date', render: when },
  ] },
  { key: 'collected_total', label: 'Collected', money: true, listKey: 'collected_list', columns: [
    { label: '#', render: n }, { label: 'Customer', render: who },
    { label: 'Collected', align: 'right', render: (r) => formatMoney(r.collected) }, { label: 'Date', render: when },
  ] },
  { key: 'billed_total', label: 'Billed', money: true, listKey: 'billed_list', columns: [
    { label: '#', render: n }, { label: 'Customer', render: who },
    { label: 'Billed', align: 'right', render: (r) => formatMoney(r.billed) }, { label: 'Date', render: when },
  ] },
  { key: 'paid_services', label: 'Paid services', listKey: 'paid_service_list', columns: [
    { label: '#', render: n }, { label: 'Service', render: (r) => r.service }, { label: 'Customer', render: who },
    { label: 'Date', render: when },
  ] },
  { key: 'tax_total', label: 'Tax', money: true },
  { key: 'average_bill', label: 'Average bill', money: true, sub: (d) => `${d.bills || 0} bills` },
];

/** Branch analytics: one branch at a time (or all), with date range and drill-down lists. */
export default function Analytics() {
  const { branches, selectedBranches } = useBranches();
  const [franchiseId, setFranchiseId] = useState(() => String(selectedBranches[0]?.id || ''));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detail, setDetail] = useState(null);
  const data = useAsync(() => api.analytics({ franchise_id: franchiseId, from, to }), [franchiseId, from, to]);
  const d = data.data || {};

  return (
    <div className="dp-page">
      <div className="dp-page-head">
        <div>
          <h1>Analytics</h1>
          <p className="dp-help">Customers, services, payments and pending amount for a branch.</p>
        </div>
      </div>

      <div className="dp-toolbar">
        <label className="dp-field dp-field--inline">
          <span>Branch</span>
          <select className="dp-input" value={franchiseId} onChange={(e) => setFranchiseId(e.target.value)}>
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="dp-field dp-field--inline">
          <span>From</span>
          <input type="date" className="dp-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="dp-field dp-field--inline">
          <span>To</span>
          <input type="date" className="dp-input" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {from || to ? (
          <button type="button" className="dp-link" onClick={() => { setFrom(''); setTo(''); }}>
            Clear dates
          </button>
        ) : null}
      </div>

      {data.error ? <Notice tone="error">{data.error}</Notice> : null}
      {!data.data && !data.error ? (
        <Loader text="Loading analytics…" />
      ) : (
        <>
          <div className={`dp-tiles${data.loading ? ' is-loading' : ''}`}>
            {TILES.map((t) => {
              const value = t.money ? formatMoney(d[t.key]) : d[t.key] ?? 0;
              const clickable = Boolean(t.listKey);
              return (
                <button
                  key={t.key}
                  type="button"
                  className={`dp-tile${clickable ? ' is-clickable' : ''}`}
                  onClick={() => clickable && setDetail(t)}
                  disabled={!clickable}
                >
                  <span>{t.label}</span>
                  <strong>{value}</strong>
                  <small>{t.sub ? t.sub(d) : clickable ? 'Tap to view' : ''}</small>
                </button>
              );
            })}
          </div>

          <div className="dp-split">
            <section className="dp-card">
              <h3>Top services</h3>
              <DetailTable
                rows={d.top_services || []}
                empty="No services yet"
                columns={[
                  { label: 'Service', render: (r) => r.name },
                  { label: 'Taken', align: 'right', render: (r) => r.count },
                ]}
              />
            </section>
            <section className="dp-card">
              <h3>Recent bills</h3>
              <DetailTable
                rows={d.recent || []}
                empty="No bills yet"
                columns={[
                  { label: 'Customer', render: who },
                  { label: 'Status', render: (r) => <BillStatus status={r.status} /> },
                  { label: 'Total', align: 'right', render: (r) => formatMoney(r.total) },
                  { label: 'Paid', align: 'right', render: (r) => formatMoney(r.paid) },
                  { label: 'Pending', align: 'right', render: (r) => formatMoney(r.pending) },
                ]}
              />
            </section>
          </div>
        </>
      )}

      {detail ? (
        <Modal title={detail.label} eyebrow="Analytics" size="wide" onClose={() => setDetail(null)}>
          <DetailTable rows={d[detail.listKey] || []} columns={detail.columns} />
        </Modal>
      ) : null}
    </div>
  );
}
